import React, { useEffect, useRef } from 'react';
import { MessageSquare } from 'lucide-react';
import { RECORD_VIEW_CONFIG } from '../constants';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../utils/i18n';

interface ViewTabsProps {
  view: string;
  viewCounts: Record<string, number>;
  onChange: (view: string) => void;
  idBase: string;
  strings?: WidgetStrings;
  visibleViews?: string[];
}

const ViewTabs = React.memo(function ViewTabs({
  view,
  viewCounts,
  onChange,
  idBase,
  strings = DEFAULT_WIDGET_STRINGS,
  visibleViews,
}: ViewTabsProps) {
  const entries = Object.entries(RECORD_VIEW_CONFIG).filter(
    ([key]) => !visibleViews || visibleViews.includes(key)
  );
  const keys = entries.map(([key]) => key);
  const previousViewRef = useRef(view);

  useEffect(() => {
    if (previousViewRef.current === view) return;
    previousViewRef.current = view;

    const activeTab = document.getElementById(`${idBase}-tab-${view}`);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    activeTab?.scrollIntoView?.({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [idBase, view]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    const currentIndex = keys.indexOf(view);
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
      const step = (e.key === 'ArrowRight') !== rtl ? 1 : -1;
      nextIndex = (currentIndex + step + keys.length) % keys.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = keys.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    const nextKey = keys[nextIndex];
    onChange?.(nextKey);
    e.currentTarget.parentElement
      ?.querySelectorAll<HTMLButtonElement>('[role=tab]')
      .item(nextIndex)
      ?.focus();
  };

  return (
    <div
      className="rw-view-tabs px-4 pb-3 flex gap-2 overflow-x-auto"
      role="tablist"
      aria-label={strings.contentViewsAria}
      aria-orientation="horizontal"
    >
      {entries.map(([key, config]) => {
        const Icon = config.icon || MessageSquare;
        const active = key === view;
        const count = key === 'chat' ? viewCounts?.chat : viewCounts?.[key];
        const tabId = `${idBase}-tab-${key}`;
        const panelId = `${idBase}-panel-${key}`;
        const title = strings[config.label];
        const label = typeof count === 'number' ? `${title} (${count})` : title;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            aria-label={label}
            id={tabId}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange?.(key)}
            onKeyDown={handleKeyDown}
            className={`rw-view-tab flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition ${
              active
                ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            } focus:outline-2 focus:outline-blue-500`}
            type="button"
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="font-medium">{title}</span>
            {typeof count === 'number' && (
              <span
                data-rw-count={count}
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  active ? 'bg-white/20 text-white' : 'bg-white text-gray-800'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});

export default ViewTabs;
